/* eslint-disable spire/export-styles */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
// import TextField, { TextFieldPresentationProps, TextFieldProps } from "@insite/mobius/TextField"; // Poolcorp custom code
import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

/* Poolcorp custom code - start */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import TextField, {TextFieldPresentationProps, TextFieldProps} from "@store/MobiusOverrides/TextField/TextField";
import { quantityInputStyles } from "@store/theme/common/mixins";
import { quantityDecrementButtonProps, quantityIncrementButtonProps } from "@store/theme/common/props/productQuantityOrdered";
/* Poolcorp custom code - end */

/* Poolcorp custom code - start */
export type ProductQuantityOrderedStyles = {
    wrapper?: InjectableCss;
    decrementButton?: ButtonPresentationProps;
    incrementButton?: ButtonPresentationProps;
    textField?: TextFieldPresentationProps;
};
/* Poolcorp custom code - end */

interface OwnProps extends TextFieldProps {
    configurationCompleted?: boolean;
    configurationSelection?: SafeDictionary<string>;
    variantSelectionCompleted?: boolean;
    labelOverride?: React.ReactNode;
    extendedStyles?: ProductQuantityOrderedStyles; // Poolcorp custom code
}

const mapStateToProps = (
    state: ApplicationState,
    props: {
        configurationCompleted?: boolean;
        variantSelectionCompleted?: boolean;
        configurationSelection?: SafeDictionary<string>;
    } & HasProductContext,
) => {
    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasProductContext;

/* Poolcorp custom code - start */


export const productQuantityOrderedStyles: ProductQuantityOrderedStyles = {
    decrementButton: quantityDecrementButtonProps,
    incrementButton: quantityIncrementButtonProps,
    textField: {
        sizeVariant: "small",
        css: css`
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
        `,
        cssOverrides: {
            formInputWrapper: css`
                display: flex;
                flex-direction: row;
                margin-top: 0 !important;
            `,
            formField: css`
                margin-top: 0;
                display: flex;
                flex-direction: row;
                align-items: center;
                gap: 0.25rem;
                width: auto;
            `,
            inputSelect: css`
                ${quantityInputStyles()}
                width: 2.5rem;
                height: 2rem;
                border-width: 1px;
                border-radius: 0;
                padding-left: 0;
                padding-right: 0;
            `,
        }
    },
};
/* Poolcorp custom code - end */

const ProductQuantityOrdered = ({
    hasEnoughInventory,
    productContext: {
        product: { id: productId },
        onQtyOrderedChanged,
        productInfo: { qtyOrdered, failedToLoadInventory },
    },
    labelOverride,
    extendedStyles,
    canAddToCart,
    configurationCompleted,
    configurationSelection,
    variantSelectionCompleted,
    ...otherProps
}: Props) => {
    const [styles] = useState(() => mergeToNew(productQuantityOrderedStyles, extendedStyles));
    const [productQty, setProductQty] = useState(qtyOrdered.toString());

    useEffect(() => {
        if (qtyOrdered === Number(productQty)) {
            return;
        }
        setProductQty(`${qtyOrdered || ""}`);
    }, [qtyOrdered]);

    if ((!hasEnoughInventory && !failedToLoadInventory) || !canAddToCart) {
        return null;
    }

    const keyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.which === 13) {
            (event.target as any).blur();
        }
    };

    /* Poolcorp custom code - start */
    const tryOnQtyOrderedChanged = (value: string) => {
        if (!onQtyOrderedChanged) {
            logger.warn(`There was no onQtyOrderedChanged passed to the ProductContext for ${productId}`);
            return;
        }

        const possibleNumber = Number(value);
        if (!Number.isNaN(possibleNumber)) {
            onQtyOrderedChanged(possibleNumber);
        }
    }

    const qtyChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setProductQty(value);

        // Poolcorp custom code - extracted function to reduce re-using code.
        tryOnQtyOrderedChanged(value);
    };

    /* Poolcorp custom code - start */

    const minValue = 1;
    const maxValue = 999999;

    const clampValue = (value:number, minValue: number, maxValue: number) => Math.max(minValue, Math.min(value, maxValue));

    const updateQty = (value: number) => {
        const nextValue = clampValue(value, minValue, maxValue).toString();
        setProductQty(nextValue);
        tryOnQtyOrderedChanged(nextValue);
    };
    
    const decrementQty = () => updateQty(Number(productQty) - 1);
    const incrementQty = () => updateQty(Number(productQty) + 1);

    /* Poolcorp custom code - end */

    return (
        <StyledWrapper {...styles.wrapper}>
            <TextField
                label={labelOverride ?? translate("QTY")}
                value={productQty}
                type="number"
                min={minValue} // Poolcorp custom code
                max={maxValue} // Poolcorp custom code
                data-test-selector="product_qtyOrdered"
                {...otherProps}
                onKeyPress={keyPressHandler}
                onChange={qtyChangeHandler}
                {...styles.textField} // Poolcorp custom code
                beforeOrnament={<Button {...styles.decrementButton} onClick={decrementQty} disabled={Number(productQty) <= minValue}>-</Button>} // Poolcorp custom code
                afterOrnament={<Button {...styles.incrementButton} onClick={incrementQty} disabled={Number(productQty) >= maxValue}>+</Button>} // Poolcorp custom code
            />
        </StyledWrapper>
    );
};

export default withProductContext(connect(mapStateToProps)(ProductQuantityOrdered));
